import { useEffect, useState } from 'react'
// import { useDispatch } from 'react-redux'
import { match, P } from 'ts-pattern'
import { createContainer } from 'unstated-next'
import { AccessIntroMembershipEnum } from '@/constants/access-intro-membership.enum'
import ROUTES from '@/constants/legacy/constRoutes'
import { LocationEnum } from '@/constants/location.enum'
import { MembershipStatusEnum } from '@/constants/membership-status.enum'
import { QueryStringKeyEnum } from '@/constants/query-string-key.enum'
import useMembershipEventTracker from '@/containers/event-tracker/membership.event-tracker'
import { useCustomRouter } from '@/containers/hooks'
import { UserContainer } from '@/containers/users/UserContainer'
import { useMembershipInvitationCodeLazyQuery } from '@/graphql/generated/hooks'
// import { doSetGlobalToastContent } from '@/stores/reduxUI'
import { isAbleMembership, isInAppFlag } from '@/utils/utilBridge'
import { localMembershipInvitationCode } from '@/utils/utilLocalStorage'

type QueryParamType = {
  [QueryStringKeyEnum.InvitationCode]: string
  [QueryStringKeyEnum.FromOneLink]: string
  [QueryStringKeyEnum.Location]: LocationEnum
}

const membershipIntroContainer = () => {
  // const dispatch = useDispatch()
  const { me, meIsLoading, initTokenSignIn, refreshUserInfo, goToLoginWebAppRouter } = UserContainer.useContainer()
  const { query, replace, push } = useCustomRouter()
  const { [QueryStringKeyEnum.InvitationCode]: paramInvitationCode, [QueryStringKeyEnum.Location]: location } =
    query as QueryParamType
  const [invitationCode, setInvitationCode] = useState<string>('')
  const [showWarning, setShowWarning] = useState(false)
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [modalTitle, setModalTitle] = useState<string>('')
  const [modalDescription, setModalDescription] = useState<string>('')
  const [showAppUpdateModal, setShowAppUpdateModal] = useState<boolean>(false)
  const [isTracking, setIsTracking] = useState<boolean>(false)
  const { trackClickMembershipIntroJoin, trackViewMembershipIntro } = useMembershipEventTracker()

  const [membershipInvitationLazyQuery, { loading: isLoadValidInviteCode }] = useMembershipInvitationCodeLazyQuery()
  const [accessMembershipIntroType, setAccessMembershipIntroType] = useState<AccessIntroMembershipEnum>()

  // 로그인, 멤버십 가입 여부 확인
  useEffect(() => {
    ;(async () => {
      const user = me?.id ? await refreshUserInfo() : await initTokenSignIn()

      if (user && user?.membershipStatus !== MembershipStatusEnum.None) {
        setIsVisible(true)
        setModalTitle('이미 핏펫플러스를 이용 중이에요')
        setModalDescription('핏펫플러스 홈으로 이동합니다.')
        return
      }

      // 기가입 여부 확인 후 초대코드 처리
      setInvitationCodeByQuery()
    })()
  }, [String(query?.pathname), me?.id, paramInvitationCode])

  // 앱 버전 업데이트 필요
  const needAppUpdate = isInAppFlag ? !isAbleMembership : false

  // 페이지 하단 유의사항 펼침
  const toggleShowWarning = () => {
    setShowWarning(!showWarning)
  }

  // 초대코드 쿼리파람 json parse 처리 (인앱에선 ?invitationCode=["code", "code"] 형식으로 오는 이슈로 재가공)
  const getInvitationCode = (paramInvitationCode: string) => {
    if (!isInAppFlag) {
      return paramInvitationCode
    }
    let invitationCode
    try {
      if (JSON.stringify(paramInvitationCode).startsWith('[')) {
        invitationCode = paramInvitationCode[0]
      } else {
        invitationCode = paramInvitationCode
      }
    } catch (e) {
      invitationCode = paramInvitationCode
    }
    return invitationCode
  }

  // 초대코드 처리
  const setInvitationCodeByQuery = () => {
    //파람이 없는 구버전 인앱 원링크 진입시 버전 체크 후 업데이트 유도 모달 노출

    if (!paramInvitationCode) {
      if (needAppUpdate) {
        setShowAppUpdateModal(true)
      }
      setAccessMembershipIntroType(me?.hasMembership ? AccessIntroMembershipEnum.Re : AccessIntroMembershipEnum.First)
      return
    }
    const invitationCode = getInvitationCode(paramInvitationCode)

    // json parse 처리된 초대코드로 백엔드 검증
    membershipInvitationLazyQuery({
      variables: {
        code: invitationCode,
      },
      onCompleted: (data) => {
        // 기본적으로 초대 검증 fetch 후 localStorage 초기화
        localMembershipInvitationCode.remove()

        // fetched data가 없으면 에러메세지 출력
        if (!data?.membershipInvitation) {
          setIsVisible(true)
          setModalTitle('유효한 초대 링크가 아니에요')
          setModalDescription('패밀리 대표에게 다시 초대를 요청하세요.')
          return
        }

        const { isExpired, isOwnerActive, canInvite } = data.membershipInvitation
        // 초대 코드 만료
        if (isExpired) {
          setIsVisible(true)
          setModalTitle('패밀리 멤버십 초대장의 유효기간이 지났어요')
          setModalDescription('패밀리 대표에게 다시 초대를 요청하세요.')
          return
        }

        // 패밀리 대표가 핏펫 플러스를 해지한 경우
        if (!isOwnerActive) {
          setIsVisible(true)
          setModalTitle('가입할 수 없는 초대장 링크예요')
          setModalDescription('패밀리 대표가 더이상 멤버십 회원이 아니에요.\n대표에게 다시 확인해주세요.')
          return
        }

        // 이벤트 트래커에서 판단하는 초대받은 유저는 초대코드 자체에 문제가 없는 경우 입니다. 따라서 유효한 기준을 패밀리 인원이 꽉찬경우를 제외합니다.
        setAccessMembershipIntroType(AccessIntroMembershipEnum.Family)

        // 패밀리 인원이 꽉찬 경우
        if (!canInvite) {
          setIsVisible(true)
          setModalTitle('초대 받으신 핏펫플러스 패밀리의\n인원이 전부 찼어요')
          setModalDescription('초대 링크를 보낸 대표에게 문의 해주세요.')
          return
        }

        // 초대 코드가 유효하면 local storage, state 저장
        localMembershipInvitationCode.save(invitationCode)
        setInvitationCode(invitationCode)

        if (needAppUpdate) {
          setShowAppUpdateModal(true)
        }
      },
    })
  }

  const handleClickBottomButton = () => {
    if (!accessMembershipIntroType) {
      return
    }
    trackClickMembershipIntroJoin({
      location,
      membershipUsertype: getUserType(),
      buttonText: getBottomButtonText(),
    })

    // 구버전 앱에서 초대링크로 들어올 경우, 클릭시마다 업데이트 모달 노출 (1순위 노출)
    if (needAppUpdate) {
      setShowAppUpdateModal(true)
      return
    }
    // 로그인 하지 않은 유저
    if (!me) {
      // 초대코드 유무에 따라서 return url 구분
      const returnUrl = invitationCode ? ROUTES.MEMBERSHIP.JOIN.FAMILY.BASE : ROUTES.MEMBERSHIP.JOIN.OWNER.BASE
      goToLoginWebAppRouter(returnUrl, { [QueryStringKeyEnum.IsFullscreen]: true })
      return
    }
    if (invitationCode) {
      push({ pathname: ROUTES.MEMBERSHIP.JOIN.FAMILY.BASE, query: { [QueryStringKeyEnum.IsFullscreen]: true } })
      return
    }
    // 미가입 상태 일때
    if (me?.membershipStatus === MembershipStatusEnum.None) {
      push({ pathname: ROUTES.MEMBERSHIP.JOIN.OWNER.BASE, query: { [QueryStringKeyEnum.IsFullscreen]: true } })
      return
    }
    // 맴버십 가입된 상태
    push(ROUTES.MEMBERSHIP.INDEX)
  }

  const getBottomButtonText = () => {
    if (needAppUpdate || !me) {
      return invitationCode ? '패밀리 멤버십 시작하기' : '지금 가입하면 첫 달 무료'
    }
    if (invitationCode) {
      return '패밀리 멤버십 시작하기'
    }
    if (me?.membershipStatus === MembershipStatusEnum.None) {
      return me?.hasMembership ? '다시 시작하기' : '지금 가입하면 첫 달 무료'
    }
    return '지금 가입하면 첫 달 무료'
  }

  const getUserType = () => {
    return match({
      invitationCode,
      membershipStatus: me?.membershipStatus,
      hasMembership: me?.hasMembership,
    })
      .with({ membershipStatus: MembershipStatusEnum.None, invitationCode: P.when((value) => !!value) }, () => {
        return 'family'
      })
      .with({ membershipStatus: MembershipStatusEnum.None, hasMembership: true }, () => 'owner_restart')
      .with({ membershipStatus: MembershipStatusEnum.None, hasMembership: false }, () => 'owner_first')
      .otherwise(() => '')
  }

  const handleClickModalOk = () => {
    setIsVisible(false)
    if (me?.membershipStatus === MembershipStatusEnum.Owner || me?.membershipStatus === MembershipStatusEnum.Family) {
      replace(ROUTES.MEMBERSHIP.INDEX)
    } else {
      replace(ROUTES.INDEX)
    }
  }

  useEffect(() => {
    if (!isTracking && !meIsLoading) {
      trackViewMembershipIntro({ location, membershipUsertype: getUserType() })
      setIsTracking(true)
    }
  }, [isTracking, meIsLoading])

  return {
    // 유저 정보
    me,
    meIsLoading,

    // 초대코드
    invitationCode,
    isLoadValidInviteCode,
    accessMembershipIntroType,

    // 하단 유의사항
    showWarning,
    toggleShowWarning,

    // 안내 모달
    visible: isVisible,
    modalOnOk: handleClickModalOk,
    modalTitle,
    modalDescription,

    // 업데이트 유도
    needAppUpdate,
    showAppUpdateModal,
    setShowAppUpdateModal,

    // 가입 버튼
    handleClickBottomButton,
    getBottomButtonText,

    // 트래커
    getUserType,
  }
}

export const MembershipIntroContainer = createContainer(membershipIntroContainer)
